import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Mail, Lock, Loader2, ArrowRight, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { AuthShell } from "@/components/AuthShell";
import { GoogleAuthButton } from "@/components/GoogleAuthButton";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectParam = new URLSearchParams(location.search).get("redirect");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success(`Welcome back, ${res.user.name}!`);
      // Honour explicit redirect (e.g. from Landing "Start Coaching")
      if (redirectParam) {
        navigate(redirectParam);
        return;
      }
      // Priority: enrolled learners land in their newest course → survey → dashboard.
      if (res.user.enrolled_courses && res.user.enrolled_courses.length > 0) {
        const latest = [...res.user.enrolled_courses].sort((a, b) =>
          String(b.enrolled_at || "").localeCompare(String(a.enrolled_at || ""))
        )[0];
        navigate(`/courses/${latest.course_id}`);
      } else if (!res.user.survey_completed) {
        navigate("/survey");
      } else {
        const purpose = res.user.survey_purpose;
        navigate(purpose === "Immigration" ? "/immigration" : "/coaching");
      }
    } else {
      toast.error(res.error);
    }
  };

  return (
    <AuthShell
      eyebrow="Welcome back"
      title="Log in to continue your journey"
      subtitle="Pick up where you left off — batches, mocks, mentor chats, all in one place."
    >
      <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
        <div>
          <Label htmlFor="email" className="text-brand-navy font-semibold">Email</Label>
          <div className="mt-2 relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-navy/40" />
            <Input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="pl-11 h-12 rounded-xl border-brand-navy/15"
              data-testid="login-email-input"
            />
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label htmlFor="password" className="text-brand-navy font-semibold">Password</Label>
            <Link to="/forgot-password" data-testid="login-forgot-link" className="text-xs font-semibold text-brand-red hover:underline">
              Forgot password?
            </Link>
          </div>
          <div className="mt-2 relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-navy/40" />
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="pl-11 pr-12 h-12 rounded-xl border-brand-navy/15"
              data-testid="login-password-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-navy/50 hover:text-brand-red transition p-1"
              aria-label={showPassword ? "Hide password" : "Show password"}
              data-testid="login-password-toggle"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <Button
          type="submit"
          disabled={loading}
          data-testid="login-submit-btn"
          className="w-full h-12 rounded-full bg-brand-red hover:bg-brand-red-hover text-white font-semibold brand-shadow btn-scale"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Log in <ArrowRight className="w-4 h-4 ml-2" /></>}
        </Button>
      </form>

      <div className="my-6 flex items-center gap-4">
        <div className="flex-1 h-px bg-brand-navy/10" />
        <span className="text-xs uppercase tracking-widest text-brand-navy/40 font-semibold">or</span>
        <div className="flex-1 h-px bg-brand-navy/10" />
      </div>

      <GoogleAuthButton redirectTo={redirectParam} testId="login-google-btn" />

      <p className="mt-8 text-center text-sm text-brand-navy/60">
        New here?{" "}
        <Link
          to={redirectParam ? `/register?redirect=${encodeURIComponent(redirectParam)}` : "/register"}
          data-testid="login-goto-register"
          className="text-brand-red font-semibold hover:underline"
        >
          Create an account
        </Link>
      </p>
    </AuthShell>
  );
}
